import {
  Component,
  computed,
  effect,
  EffectRef,
  ElementRef,
  model,
  ModelSignal,
  signal,
  Signal,
  untracked,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCard, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatChipOption } from '@angular/material/chips';
import { Router } from '@angular/router';
import {
  AlertComponent,
  ButtonComponent,
  ExpanderComponent,
  ExpanderContentComponent,
  PageHeaderComponent,
  SideSheetService,
  SplitButtonComponent,
} from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { DialogService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import {
  CartCalculationSummaryModel,
  CartItemModel,
  CartModel,
  ShopProductListItemBaseModel,
  TopLevelCategoryNode,
  TrackingProductModel,
  VoucherListItem,
} from '@libs/shop/models';
import {
  CartQueueItem,
  CartService,
  CategoryNodeService,
  ConfirmationDialogModule,
  ConfirmationDialogService,
  EsaInfo,
  EsaService,
  ViewportService,
} from '@libs/shared/services';
import { CartTrackingService } from '@libs/shared/tracking';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { OrderBottomBarComponent } from '../order-bottom-bar/order-bottom-bar.component';
import { OrderItemCategoryListComponent } from '../order-item-category-list/order-item-category-list.component';
import { OrderOverviewComponent } from '../order-overview/order-overview.component';
import { OrderOverviewDetailsComponent } from '../order-overview/order-overview-details/order-overview-details.component';
import { OrderVouchersComponent } from '../order-vouchers/order-vouchers.component';
import { ProductAvailabilityDialogFormComponent } from '../product-availability-dialog-form/product-availability-dialog-form.component';
import { ProductCategoriesFilterComponent } from '../../elements/product-categories-filter/product-categories-filter.component';
import { CrossSellingSliderComponent } from '../../catalog/cross-selling-slider';
import { DeliveryWindowsDialogComponent } from '../../delivery-windows-form/delivery-windows-dialog';

@Component({
  selector: 'ui-shopping-cart',
  imports: [
    AlertComponent,
    ButtonComponent,
    ConfirmationDialogModule,
    DeliveryWindowsDialogComponent,
    ExpanderComponent,
    IconComponent,
    NgTemplateOutlet,
    OrderOverviewComponent,
    OrderVouchersComponent,
    ProductCategoriesFilterComponent,
    SplitButtonComponent,
    OrderItemCategoryListComponent,
    MatChipOption,
    PageHeaderComponent,
    OrderBottomBarComponent,
    CrossSellingSliderComponent,
    MatCard,
    MatCardTitle,
    MatCardHeader,
    ExpanderContentComponent,
    OrderOverviewDetailsComponent,
  ],
  templateUrl: './shopping-cart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./shopping-cart.component.scss'],
})
export class ShoppingCartComponent {
  public readonly cart: Signal<CartModel | undefined> = signal(undefined);
  public readonly calculationSummary: Signal<CartCalculationSummaryModel | undefined> = computed(() => this.cart()?.totals);
  public readonly esaInfo: Signal<EsaInfo> = computed(() => this.esaService.getEsaInfo(this.cart()?.items));

  public items: Signal<CartItemModel[]> = computed(() => this.cart()?.items ?? []);

  public nodes: Signal<TopLevelCategoryNode<CartItemModel>[]> = computed(() => this.categoryNodeService.createCategoryNodes<CartItemModel>(this.items()));
  public selectedNodes: ModelSignal<TopLevelCategoryNode<CartItemModel>[]> = model([] as TopLevelCategoryNode<CartItemModel>[]);
  public visibleNodes: Signal<TopLevelCategoryNode<CartItemModel>[]> = computed(() => {
    if (!this.selectedNodes()?.length) {
      return this.nodes();
    }

    return this.selectedNodes();
  });

  public readonly calculationSummaryElement: Signal<ElementRef<HTMLElement> | undefined> = viewChild<ElementRef<HTMLElement>>('calculationSummaryElement');
  public cartQueue: Signal<CartQueueItem[]> = toSignal(this.cartService.getCartQueue(), { initialValue: [] });
  public isCartQueueLoading: Signal<boolean> = computed(() => this.cartQueue().length > 0);

  public deliveryWindowsRefDate: Signal<DateTime> = signal(DateTime.now());

  public readonly expandedMap: Map<string, boolean> = new Map([]);

  public readonly isMobile: Signal<boolean>;
  public skeletonRenderArray: Signal<number[]> = signal(new Array<number>(5).fill(0));

  public voucherItems: Signal<VoucherListItem[]> = toSignal(this.cartService.getVoucherListItems(), { initialValue: [] });
  public orderVouchersEnabled: Signal<boolean> = this.capabilityService.isAvailable('shop.orderVouchers');

  public orderBottomBarVisible: Signal<boolean> = computed(() => this.isMobile() && this.capabilityService.isAvailable('shop.newCheckoutUi')());

  public showVoucherInput: WritableSignal<boolean> = signal(false);

  public constructor(
    private readonly cartService: CartService,
    private readonly router: Router,
    private readonly esaService: EsaService,
    private readonly categoryNodeService: CategoryNodeService,
    private readonly viewportService: ViewportService,
    private readonly dialogService: DialogService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly capabilityService: CapabilityService,
    private readonly cartTrackingService: CartTrackingService,
    private readonly sideSheetService: SideSheetService,
  ) {
    this.cart = toSignal(this.cartService.getCart());
    this.isMobile = toSignal(this.viewportService.isMobile(), { initialValue: false });

    const showVoucherEffectRef: EffectRef = effect(() => {
      const cart: CartModel | undefined = this.cart();

      if (!cart) {
        return;
      }

      if (cart.vouchers?.length) {
        this.showVoucherInput.set(true);
      }

      // it should not reshow the vouchers if the user collapsed it manually.
      showVoucherEffectRef.destroy();
    });

    effect(() => {
      let cart: CartModel | undefined;

      untracked(() => {
        cart = this.cart();
      });

      if (!this.isCartQueueLoading() && cart) {
        this.cartTrackingService.trackCheckoutStep(cart, 'cart');
      }
    });
  }

  public onItemQuantityChanged(item: ShopProductListItemBaseModel): void {
    const oldCartItem: CartItemModel | undefined = this.cart()?.items.find(x => x.id === item.id);

    void this.cartService.queueCartItem('changeQuantity', {
      sku: item.sku,
      cartItem: item as CartItemModel,
    });

    if (oldCartItem) {
      this.cartTrackingService.trackCartQuantityChange(item.quantity, oldCartItem);
    }
  }

  public onItemRemoved(item: ShopProductListItemBaseModel): void {
    void this.cartService.queueCartItem('remove', {
      sku: item.sku,
      cartItem: item as CartItemModel,
    });
  }

  /* istanbul ignore next */
  public goToCheckout(): Promise<boolean> {
    return this.router.navigateByUrl('checkout');
  }

  public openProductAvailabilityInfo(): void {
    this.dialogService.open(ProductAvailabilityDialogFormComponent);
  }

  public categoryExpandedChanged(expanded: boolean, node: TopLevelCategoryNode<CartItemModel>): void {
    this.expandedMap.set(node.label, expanded);
  }

  public deleteAllItemsInCart(): void {
    void firstValueFrom(this.confirmationDialogService.open({
      title: 'Möchten Sie wirklich alle Artikel aus Ihrem Warenkorb entfernen?',
      confirm: 'Alle Artikel löschen',
      decline: 'Abbrechen',
    })).then((confirmation) => {
      const cart: CartModel | undefined = this.cart();

      if (confirmation) {
        void firstValueFrom(this.cartService.deleteAllItemsFromCart());
        this.sideSheetService.closeSideSheet('right');

        /* istanbul ignore else */
        if (cart) {
          const products: TrackingProductModel[] = cart.items.map((x, index) => ({
            type: 'product',
            ...x.productData,
            position: index,
            quantity: x.quantity,
          }));

          this.cartTrackingService.trackRemoveFromCartProducts(products);
        }
      }
    });
  }

  public toggleShowVoucherInput(): void {
    this.showVoucherInput.set(!this.showVoucherInput());
  }
}
